'use es6';

import { useTheme } from 'foundations-theming/utils';
import UIStatusTag from 'UIComponents/tag/UIStatusTag';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIButton from 'UIComponents/button/UIButton';
import H5 from 'UIComponents/elements/headings/H5';
import { WEB_FONT_FAMILY } from 'HubStyleTokens/misc';
import { useEffect, useState } from 'react';
import { getSuspensionStatus } from '../api/suspensionStatus';
import reviewState from '../constants/reviewState';
import InformationModal from './InformationModal';
import ContentData from '../records/ContentData';
import { BulkContent } from '../records/BulkContentReview';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { EERIE } from 'HubStyleTokens/colors';
import usePrevious from 'react-utils/hooks/usePrevious';
import isEqual from 'hs-lodash/isEqual';
import getEventKey from '../utils/getEventKey';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const WarningTag = ({
  contentId,
  contentReviewData,
  withBulkData,
  keyWord,
  usageTracker,
  inline = false,
  isPublished
}) => {
  const trellisTheme = useTheme();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [contentType, setContentType] = useState('content');
  const [slaCopyType, setSlaCopyType] = useState(null);
  const prevContentId = usePrevious(contentId);
  useEffect(() => {
    let cancel = false;
    if (!withBulkData && !isEqual(prevContentId, contentId)) {
      getSuspensionStatus(contentId, keyWord).then(data => {
        if (cancel) return;
        const contentData = ContentData(Object.assign({}, data, {
          contentType: keyWord
        }));
        setStatus(contentData.get('reviewState'));
        setContentType(contentData.get('contentType'));
        setSlaCopyType(contentData.get('slaCopyType'));
      }).catch(() => setStatus(null));
    }
    return () => {
      cancel = true;
    };
  }, [contentId, withBulkData]); // eslint-disable-line

  useEffect(() => {
    if (withBulkData && contentReviewData && contentReviewData.get('reviewList') && contentReviewData.get('reviewList').has(contentId.toString())) {
      const reviewContent = contentReviewData.getIn(['reviewList', contentId.toString()]);
      setStatus(reviewContent.get('reviewState'));
      setContentType(contentReviewData.get('contentType'));
      setSlaCopyType(reviewContent.get('slaCopyType'));
    }
  }, [contentReviewData, withBulkData, contentId]);
  const handleClick = e => {
    if (usageTracker) {
      usageTracker.track(getEventKey(keyWord), {
        action: 'open-close-modal-click'
      });
    }
    e.stopPropagation();
    setOpen(!open);
  };
  const determineLabelType = () => {
    if (keyWord === 'form' && isPublished) return null;
    if (status === reviewState.BLOCKED) return 'danger';
    if (status === reviewState.IN_REVIEW) return 'warning';
    return null;
  };
  const labelType = determineLabelType();
  const showLabel = !!labelType;
  useEffect(() => {
    if (showLabel && usageTracker) {
      usageTracker.track(getEventKey(keyWord), {
        action: 'warning-tag-loaded'
      });
    }
  }, [showLabel]); // eslint-disable-line

  const tooltipPlacement = withBulkData ? 'bottom' : 'bottom right';
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [open && /*#__PURE__*/_jsx(InformationModal, {
      toggleModal: handleClick,
      status: status,
      contentType: contentType,
      slaCopyType: slaCopyType,
      usageTracker: usageTracker
    }), showLabel && /*#__PURE__*/_jsx(UITooltip, {
      placement: tooltipPlacement,
      use: "longform",
      title: /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(H5, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `contentLabel.${status}.toolTipHeader`
          })
        }), /*#__PURE__*/_jsx("div", {
          "data-test-id": "tooltip-message-test",
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: `contentLabel.${status}.toolTipText.${contentType}`
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          use: "link",
          onClick: handleClick,
          "data-test-id": "tooltip-button-test",
          className: "m-top-3",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `contentLabel.${status}.toolTipButton`
          })
        })]
      }),
      children: inline ? /*#__PURE__*/_jsxs("span", {
        children: [/*#__PURE__*/_jsx(UIIcon, {
          name: "warning",
          size: "xxs",
          "data-test-id": "warning-icon",
          color: labelType === 'warning' ? trellisTheme.color['do-not-use-marigold'] : trellisTheme.color['do-not-use-candy-apple'],
          className: "m-right-2"
        }), /*#__PURE__*/_jsx(UIStatusTag, {
          use: labelType,
          className: "p-x-0"
        })]
      }) : /*#__PURE__*/_jsxs("span", {
        children: [/*#__PURE__*/_jsx(UIStatusTag, {
          use: labelType,
          className: "p-x-0 m-bottom-0"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: `contentLabel.${status}.labelText`,
          style: {
            font: WEB_FONT_FAMILY,
            fontSize: '12px',
            color: '#7C98B6'
          }
        }), /*#__PURE__*/_jsx(UIIcon, {
          name: "info",
          size: "xxs",
          "data-test-id": "info-icon",
          color: EERIE,
          className: "m-x-1"
        })]
      })
    })]
  });
};
WarningTag.propTypes = {
  contentId: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
  contentReviewData: PropTypes.instanceOf(BulkContent),
  inline: PropTypes.bool,
  keyWord: PropTypes.string.isRequired,
  withBulkData: PropTypes.bool,
  usageTracker: PropTypes.object,
  isPublished: PropTypes.bool
};
export default WarningTag;